import { todayLocalDateKey } from './logiciel-caisse-api.helper';
import {
  PosPaymentStatus,
  PosTicketAvailability,
  PosTicketDetail,
  PosTicketLine,
  PosTicketRow,
} from 'app/main/pages/POS/poscontent/poscontent.component';

/** Mappe une ligne API tickets vers le modèle tableau POS vente. */
export function mapApiTicketRowToPos(
  row: Record<string, string | unknown>
): PosTicketRow {
  const clientCell = String(row['CLIENT'] ?? '');
  const clientLines = clientCell.split('\n').map((s) => s.trim());
  const statutRaw = String(row['_ticketStatutRaw'] ?? '').trim();
  const isFactured =
    row['_isFactured'] === true ||
    row['_isFactured'] === 1 ||
    row['_isFactured'] === '1' ||
    row['_isFactured'] === 'true';
  const isConsolide =
    row['_statutConsolidation'] === true ||
    row['_statutConsolidation'] === 1 ||
    row['_statutConsolidation'] === '1' ||
    row['_statutConsolidation'] === 'true' ||
    row['_isConsolide'] === true ||
    row['_isConsolide'] === 1 ||
    row['_isConsolide'] === '1' ||
    row['_isConsolide'] === 'true';
  const hasAvoireEmis =
    row['_hasAvoireEmis'] === true ||
    row['_hasAvoireEmis'] === 1 ||
    row['_hasAvoireEmis'] === '1' ||
    row['_hasAvoireEmis'] === 'true';
  const hasVenteEmis =
    row['_hasVenteEmis'] === true ||
    row['_hasVenteEmis'] === 1 ||
    row['_hasVenteEmis'] === '1' ||
    row['_hasVenteEmis'] === 'true';
  const hasAnnulationEmis =
    row['_hasAnnulationEmis'] === true ||
    row['_hasAnnulationEmis'] === 1 ||
    row['_hasAnnulationEmis'] === '1' ||
    row['_hasAnnulationEmis'] === 'true';
  const factureDocumentId =
    row['_factureDocumentId'] != null && Number(row['_factureDocumentId']) > 0
      ? Number(row['_factureDocumentId'])
      : null;
  const documentOriginal =
    row['_documentOriginal'] != null && String(row['_documentOriginal']).trim() !== ''
      ? String(row['_documentOriginal']).trim()
      : null;
  const documentOriginalId =
    row['_documentOriginalId'] != null && Number(row['_documentOriginalId']) > 0
      ? Number(row['_documentOriginalId'])
      : null;
  const totalAmount = Number(row['_totalAmount'] ?? 0) || 0;
  const documentType =
    row['_documentType'] != null && String(row['_documentType']).trim() !== ''
      ? String(row['_documentType']).trim()
      : inferDocumentTypeFromTypeLabel(row['TYPE']);
  const documentTypeLabel =
    String(row['TYPE'] ?? '').trim() || formatDocumentTypeLabel(documentType);
  const famille =
    row['_famille'] != null && String(row['_famille']).trim() !== ''
      ? String(row['_famille']).trim()
      : documentType === 'ticket_en_traitement'
        ? 'initialisation'
        : documentType
          ? 'ticke'
          : null;
  const sousType =
    row['_sousType'] != null && String(row['_sousType']).trim() !== ''
      ? String(row['_sousType']).trim()
      : null;
  const sousTypeLabel = String(row['SOUS-TYPE'] ?? '').trim() || null;
  const cloture =
    row['_cloture'] != null && String(row['_cloture']).trim() !== ''
      ? String(row['_cloture']).trim()
      : null;
  const clotureLabel = String(row['CLOTURE'] ?? '').trim() || null;

  return {
    id: String(row['id'] ?? ''),
    numero: String(row['NUMÉRO'] ?? '—'),
    date: parseApiTicketDate(row['DATE']),
    clientName: clientLines[0] || '—',
    clientPhone: clientLines[1] || '',
    entrepot: String(row['ENTREPOT'] ?? '—'),
    entrepotId:
      row['_entrepotId'] != null && String(row['_entrepotId']).trim() !== ''
        ? String(row['_entrepotId'])
        : null,
    paymentStatus: mapPaymentStatus(String(row['PAIEMENT'] ?? '')),
    paymentType: String(row['PAIEMENT'] ?? '—'),
    ticketTotal: totalAmount,
    ticketStatus: resolvePosTicketAvailability(
      statutRaw,
      isFactured,
      documentOriginal,
      totalAmount,
      documentType,
      isConsolide
    ),
    documentStatutRaw: statutRaw || null,
    documentStatutLabel:
      String(row['STATUT'] ?? '').trim() || formatDocumentStatutLabel(statutRaw),
    factureDocumentId,
    documentOriginal,
    documentOriginalId,
    documentType,
    documentTypeLabel,
    famille,
    sousType,
    sousTypeLabel,
    cloture,
    clotureLabel,
    isConsolide,
    hasAvoireEmis,
    hasVenteEmis,
    hasAnnulationEmis,
    origine:
      row['_origine'] != null && String(row['_origine']).trim() !== ''
        ? String(row['_origine']).trim()
        : null,
    origineLabel: String(row['ORIGINE'] ?? '').trim() || '—',
  };
}

export function mapApiDetailToPosDetail(
  row: PosTicketRow,
  data: Record<string, unknown>
): PosTicketDetail {
  const docOriginal =
    data['document_original'] != null && String(data['document_original']).trim() !== ''
      ? String(data['document_original']).trim()
      : null;
  const origDetail = data['document_original_detail'] as Record<string, unknown> | null;
  const ticketRow: PosTicketRow = {
    ...row,
    documentOriginal:
      docOriginal ||
      (origDetail?.['code_document'] != null
        ? String(origDetail['code_document'])
        : row.documentOriginal),
  };
  const detailStatutRaw = String(data['statut'] ?? row.documentStatutRaw ?? '').trim();
  const hasAvoireEmis =
    data['has_avoire_emis'] === true ||
    data['has_avoire_emis'] === 1 ||
    data['has_avoire_emis'] === '1' ||
    data['has_avoire_emis'] === 'true' ||
    row.hasAvoireEmis === true;
  ticketRow.documentStatutRaw = detailStatutRaw || null;
  ticketRow.documentStatutLabel = formatDocumentStatutLabel(detailStatutRaw);
  const detailType = String(data['type'] ?? row.documentType ?? '').trim() || null;
  const lignesRaw = Array.isArray(data['lignes']) ? data['lignes'] : [];
  const lines: PosTicketLine[] = lignesRaw.map((item) => {
    const l = item as Record<string, unknown>;
    const couleur = l['couleur'] != null ? String(l['couleur']).trim() : '';
    const taille = l['taille'] != null ? String(l['taille']).trim() : '';
    const colorSize = [couleur, taille].filter(Boolean).join(' ') || '—';
    const qnt = Number(l['qnt'] ?? 0) || 0;
    const prix = Number(l['prix_unitaire'] ?? l['prix'] ?? 0) || 0;
    const remise = Number(l['remise'] ?? 0) || 0;
    const totalLigne =
      l['total'] != null && l['total'] !== ''
        ? Number(l['total']) || 0
        : prix * qnt - remise;
    const ref =
      l['code_barre'] != null && String(l['code_barre']).trim() !== ''
        ? String(l['code_barre']).trim()
        : l['libelle'] != null && String(l['libelle']).trim() !== ''
          ? String(l['libelle']).trim()
          : String(l['reference'] ?? l['code'] ?? l['code_produit'] ?? '—');
    return {
      reference: ref,
      couleur: couleur || '—',
      taille,
      colorSize,
      quantity: qnt,
      unitPrice: prix,
      lineTotal: totalLigne,
    };
  });

  let totalPrice = Number(data['total'] ?? 0) || 0;
  ticketRow.documentType = detailType;
  ticketRow.hasAvoireEmis = hasAvoireEmis;
  const hasVenteEmis =
    data['has_vente_emis'] === true ||
    data['has_vente_emis'] === 1 ||
    data['has_vente_emis'] === '1' ||
    data['has_vente_emis'] === 'true' ||
    row.hasVenteEmis === true;
  const hasAnnulationEmis =
    data['has_annulation_emis'] === true ||
    data['has_annulation_emis'] === 1 ||
    data['has_annulation_emis'] === '1' ||
    data['has_annulation_emis'] === 'true' ||
    row.hasAnnulationEmis === true;
  ticketRow.hasVenteEmis = hasVenteEmis;
  ticketRow.hasAnnulationEmis = hasAnnulationEmis;
  ticketRow.documentTypeLabel = formatDocumentTypeLabel(detailType);
  const reglement = String(data['reglement'] ?? '').trim();
  const isFactured =
    data['is_factured'] === true ||
    data['is_factured'] === 1 ||
    data['is_factured'] === '1' ||
    data['is_factured'] === 'true';
  const isConsolide =
    data['statut_consolidation'] === true ||
    data['statut_consolidation'] === 1 ||
    data['statut_consolidation'] === '1' ||
    data['statut_consolidation'] === 'true' ||
    data['consolide'] === true ||
    data['consolide'] === 1 ||
    data['consolide'] === '1' ||
    data['consolide'] === 'true';
  const factureDocumentId =
    data['facture_document_id'] != null && Number(data['facture_document_id']) > 0
      ? Number(data['facture_document_id'])
      : null;
  ticketRow.factureDocumentId = factureDocumentId;
  ticketRow.paymentStatus = mapReglementToPaymentStatus(reglement);
  ticketRow.paymentType = formatReglementLabel(reglement) || ticketRow.paymentType;
  ticketRow.ticketTotal = totalPrice;
  ticketRow.ticketStatus = resolvePosTicketAvailability(
    detailStatutRaw,
    isFactured,
    ticketRow.documentOriginal ?? null,
    totalPrice,
    detailType,
    isConsolide
  );
  ticketRow.isConsolide = isConsolide;
  const origIdRaw = data['document_original_id'];
  ticketRow.documentOriginalId =
    origIdRaw != null && Number(origIdRaw) > 0
      ? Number(origIdRaw)
      : row.documentOriginalId ?? null;
  const sousType = String(data['sous_type'] ?? row.sousType ?? '').trim();
  ticketRow.sousType = sousType || row.sousType || null;
  ticketRow.sousTypeLabel = row.sousTypeLabel || null;
  ticketRow.famille =
    data['famille'] != null && String(data['famille']).trim() !== ''
      ? String(data['famille']).trim()
      : detailType === 'ticket_en_traitement'
        ? 'initialisation'
        : row.famille || (detailType ? 'ticke' : null);
  const clotureRaw =
    data['cloture'] != null && String(data['cloture']).trim() !== ''
      ? String(data['cloture']).trim()
      : row.cloture || null;
  ticketRow.cloture = clotureRaw;
  ticketRow.clotureLabel =
    clotureRaw === 'cloture'
      ? 'Clôturé'
      : ticketRow.famille === 'initialisation'
        ? 'Non clôturé'
        : row.clotureLabel || null;

  let amountPaid = totalPrice;
  if (reglement === 'en_attente') {
    amountPaid = 0;
  } else if (reglement === 'partiellement_paient') {
    amountPaid = Math.round(totalPrice * 0.5 * 1000) / 1000;
  }
  const isAcompteTicke =
    sousType === 'acompte' || sousType === 'reservation_avec_paiement';
  const isResaInit = sousType === 'reservation_avec_acompte';
  const isInitialisation =
    ticketRow.famille === 'initialisation' || detailType === 'ticket_en_traitement';
  const linkedPaid = Number(data['montant_paye_lie'] ?? 0) || 0;
  if (isAcompteTicke) {
    amountPaid = Math.round((Number(data['total'] ?? 0) || 0) * 1000) / 1000;
    const origTotal = Number(origDetail?.['total'] ?? 0) || 0;
    if (origTotal > 0) {
      totalPrice = origTotal;
    }
  } else if (isResaInit || (isInitialisation && linkedPaid > 0)) {
    amountPaid = linkedPaid > 0 ? Math.round(linkedPaid * 1000) / 1000 : 0;
  } else if (isInitialisation && reglement !== 'paye' && reglement !== 'payé') {
    amountPaid = 0;
  }

  const resteAPayer = Math.max(
    0,
    Math.round((totalPrice - amountPaid) * 1000) / 1000
  );

  return { ticket: ticketRow, lines, totalPrice, amountPaid, resteAPayer };
}

/** Détail ticket depuis la réponse API seule (panneau origine, etc.). */
export function mapApiDetailResponseToPosDetail(data: Record<string, unknown>): PosTicketDetail {
  const stub = buildPosTicketRowStubFromDetail(data);
  return mapApiDetailToPosDetail(stub, data);
}

function buildPosTicketRowStubFromDetail(data: Record<string, unknown>): PosTicketRow {
  const clientDetail = data['client_detail'] as Record<string, unknown> | null | undefined;
  const prenom = clientDetail?.['prenom'] != null ? String(clientDetail['prenom']).trim() : '';
  const nom = clientDetail?.['nom'] != null ? String(clientDetail['nom']).trim() : '';
  const phone =
    (clientDetail?.['telephone'] != null ? String(clientDetail['telephone']).trim() : '') ||
    (clientDetail?.['phone'] != null ? String(clientDetail['phone']).trim() : '') ||
    (data['client_phone'] != null ? String(data['client_phone']).trim() : '');
  const dateRaw = data['date'] != null ? String(data['date']) : '';
  const date =
    dateRaw && /^\d{4}-\d{2}-\d{2}/.test(dateRaw)
      ? dateRaw.slice(0, 10)
      : todayLocalDateKey();
  const reglement = String(data['reglement'] ?? '').trim();
  const detailType = String(data['type'] ?? '').trim() || null;
  const totalAmount = Number(data['total'] ?? 0) || 0;
  const origDetail = data['document_original_detail'] as Record<string, unknown> | null;
  const documentOriginal =
    data['document_original'] != null && String(data['document_original']).trim() !== ''
      ? String(data['document_original']).trim()
      : origDetail?.['code_document'] != null
        ? String(origDetail['code_document'])
        : null;
  const origIdRaw = data['document_original_id'];
  return {
    id: String(data['id'] ?? ''),
    numero: String(data['code_document'] ?? '—'),
    date,
    clientName: [prenom, nom].filter(Boolean).join(' ') || '—',
    clientPhone: phone,
    entrepot:
      data['entrepot_libelle'] != null && String(data['entrepot_libelle']).trim() !== ''
        ? String(data['entrepot_libelle']).trim()
        : '—',
    entrepotId:
      data['entrepot_id'] != null && Number(data['entrepot_id']) > 0
        ? String(data['entrepot_id'])
        : null,
    paymentStatus: mapReglementToPaymentStatus(reglement),
    paymentType: formatReglementLabel(reglement) || '—',
    ticketTotal: totalAmount,
    ticketStatus: 'ticke',
    documentStatutRaw: String(data['statut'] ?? '').trim() || null,
    documentType: detailType,
    documentOriginal,
    documentOriginalId:
      origIdRaw != null && Number(origIdRaw) > 0 ? Number(origIdRaw) : null,
  };
}

/** Statut API document → filtre onglet POS (uniquement en_cours / termine). */
export function mapPosTabToApiStatut(tab: string): string | undefined {
  const key = tab.trim().toLowerCase().replace(/\s+/g, '_');
  const map: Record<string, string | undefined> = {
    tous: undefined,
    ticke: undefined,
    en_traitement: undefined,
    reservation: undefined,
    consolide: undefined,
    annule: undefined,
    avoire: undefined,
    facture_client: undefined,
  };
  return map[key];
}

/** Libellé affiché pour documents.statut (en_cours, termine uniquement). */
export function formatDocumentStatutLabel(statutRaw: string | null | undefined): string {
  const s = String(statutRaw ?? '').trim();
  if (!s) return '—';
  if (s === 'termine') return 'Terminé';
  if (s === 'en_cours') return 'En cours';
  return s;
}

function inferDocumentTypeFromTypeLabel(value: unknown): string | null {
  const label = String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (label === 'ticke' || label === 'ticket vente') {
    return 'ticket_vente';
  }
  if (
    label === 'initialisation' ||
    label === 'disponible' ||
    label === 'en_traitement' ||
    label === 'en traitement' ||
    label === 'reservation' ||
    label === 'ticket reservation'
  ) {
    return 'ticket_en_traitement';
  }
  if (label === 'facture') {
    return 'facture';
  }
  if (label === 'avoire' || label === 'avoiré') {
    return 'ticket_avoire';
  }
  if (label === 'annule' || label === 'annulé') {
    return 'ticket_annule';
  }
  return null;
}

export function formatDocumentTypeLabel(documentType: string | null | undefined): string {
  const t = String(documentType ?? '').trim();
  if (t === 'ticket_en_traitement') return 'Initialisation';
  if (
    t === 'ticket_vente' ||
    t === 'ticket_avoire' ||
    t === 'ticket_annule'
  ) {
    return 'Tické';
  }
  if (t === 'facture') return 'Facture';
  return t || '—';
}

function parseApiTicketDate(value: unknown): string {
  const raw = String(value ?? '').trim();
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  return todayLocalDateKey();
}

function mapPaymentStatus(paiementLabel: string): PosPaymentStatus {
  const s = paiementLabel.trim().toLowerCase();
  if (s === 'payé' || s === 'paye') {
    return 'paye';
  }
  if (s.includes('partiel')) {
    return 'partiel';
  }
  return 'non_paye';
}

/** Règlement API (payé, en_attente, …) → statut paiement POS. */
export function mapReglementToPaymentStatus(reglement: string): PosPaymentStatus {
  const s = reglement.trim().toLowerCase();
  if (s === 'payé' || s === 'paye') {
    return 'paye';
  }
  if (s.includes('partiel')) {
    return 'partiel';
  }
  return 'non_paye';
}

function formatReglementLabel(reglement: string): string {
  const s = reglement.trim().toLowerCase();
  if (s === 'payé' || s === 'paye') return 'Payé';
  if (s === 'en_attente') return 'En attente de règlement';
  if (s.includes('partiel')) return 'Partiellement payé';
  return reglement.trim() || '—';
}

export function resolvePosTicketAvailability(
  statutRaw: string,
  isFactured = false,
  _documentOriginal: string | null = null,
  _totalAmount = 0,
  documentType: string | null = null,
  isConsolide = false
): PosTicketAvailability {
  const t = String(documentType ?? '').trim();
  if (t === 'ticket_avoire') {
    return 'avoire';
  }
  if (t === 'ticket_annule') {
    return 'annule';
  }
  if (t === 'ticket_en_traitement') {
    return 'en_traitement';
  }
  if (t === 'ticket_vente') {
    if (isConsolide) {
      return 'consolide';
    }
    return 'ticke';
  }
  if (isFactured) {
    return 'facture_client';
  }
  if (isConsolide) {
    return 'consolide';
  }
  if (statutRaw === 'en_cours') {
    return 'en_traitement';
  }
  return 'ticke';
}

function mapTicketStatus(
  statutRaw: string,
  isFactured = false,
  documentOriginal: string | null = null,
  totalAmount = 0,
  documentType: string | null = null,
  isConsolide = false
): PosTicketAvailability {
  return resolvePosTicketAvailability(
    statutRaw,
    isFactured,
    documentOriginal,
    totalAmount,
    documentType,
    isConsolide
  );
}
